import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  DollarSign, 
  MapPin, 
  Calendar, 
  TrendingUp, 
  ExternalLink, 
  Plus, 
  Check, 
  Loader2,
  ChevronDown,
  ArrowUpDown,
  Download,
  X
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';

interface Company {
  id: string;
  name: string;
  domain: string;
  industry: string;
  subIndustry: string;
  employeeCount: number;
  employeeRange: string;
  revenue: string;
  revenueAmount: number;
  location: {
    city: string;
    state: string;
    country: string;
    headquarters: string;
  };
  description: string;
  founded: number;
  website: string;
  logo?: string;
  socialProfiles: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  technologies: string[];
  fundingInfo: {
    stage: string;
    totalFunding: string;
    lastFundingDate: string;
    lastFundingAmount: string;
    investors: string[];
  };
  businessMetrics: {
    growthRate: number;
    hiringTrend: 'up' | 'down' | 'stable';
    marketCap?: number;
    valuation?: string;
  };
  buyingIntentSignals: {
    score: number;
    signals: Array<{
      type: 'technology_change' | 'job_posting' | 'funding' | 'news' | 'leadership_change';
      description: string;
      date: string;
      weight: number;
    }>;
  };
  competitiveIntel: {
    mainCompetitors: string[];
    marketPosition: string;
    competitiveAdvantage: string[];
  };
  newsAndEvents: Array<{
    title: string;
    date: string;
    source: string;
    type: 'funding' | 'product' | 'hiring' | 'partnership' | 'acquisition' | 'award';
    summary: string;
  }>;
  keyPeople: Array<{
    name: string;
    title: string;
    linkedinUrl?: string;
    photoUrl?: string;
    department: string;
  }>;
  contactInfo: {
    mainPhone?: string;
    supportEmail?: string;
    salesEmail?: string;
    headquarters: {
      address: string;
      phone?: string;
    };
  };
  accountScoring: {
    overallScore: number;
    icpMatch: number;
    buyingIntent: number;
    engagement: number;
    timing: number;
  };
  dataSourceAttribution: string[];
  lastUpdated: string;
}

interface CompanySearchResults {
  companies: Company[];
  totalCount: number;
  page: number;
  hasMore: boolean;
  aggregations: {
    industries: Array<{ name: string; count: number }>;
    employeeRanges: Array<{ name: string; count: number }>;
    locations: Array<{ name: string; count: number }>;
    fundingStages: Array<{ name: string; count: number }>;
    technologies: Array<{ name: string; count: number }>;
  };
  searchTime: number;
  llmInsights?: any;
  marketIntelligence?: any;
}

interface CompanyResultsSectionProps {
  results: CompanySearchResults | null;
  isSearching: boolean;
  selectedCompanies: Set<string>;
  onCompanySelection: (companyId: string, selected: boolean) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBulkExport: (format: 'csv' | 'json') => void;
  sortBy: string;
  sortOrder: string;
  onSortChange: (sortBy: string, sortOrder: string) => void;
}

// Company Card Component
const CompanyCard: React.FC<{
  company: Company;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
}> = ({ company, isSelected, onSelect }) => {
  const getBuyingIntentColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  const getHiringTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      default: return '➡️';
    }
  };

  const formatFunding = (amount: string) => {
    if (!amount || amount === '0') return 'Undisclosed';
    return amount;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6 hover:shadow-lg transition-all duration-200"
    >
      {/* Header with Selection */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4 flex-1">
          <button
            onClick={() => onSelect(!isSelected)}
            className={cn(
              "w-5 h-5 rounded border-2 flex items-center justify-center transition-all mt-1",
              isSelected 
                ? "bg-primary-500 border-primary-500" 
                : "border-neutral-300 dark:border-neutral-600 hover:border-primary-400"
            )}
          >
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </button>
          
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                {company.logo || company.name.charAt(0)}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {company.name}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {company.domain}
                </p>
              </div>
            </div>
            
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 line-clamp-2">
              {company.description}
            </p>
            
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {company.employeeRange}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <DollarSign className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {company.revenue}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {company.location.city}, {company.location.state}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  Founded {company.founded}
                </span>
              </div>
            </div>
            
            {/* Tags and Indicators */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                {company.industry}
              </span>
              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                {company.fundingInfo.stage}
              </span>
              <span className={cn(
                "px-2 py-1 text-xs rounded-full",
                getBuyingIntentColor(company.buyingIntentSignals.score)
              )}>
                {company.buyingIntentSignals.score}% Intent
              </span>
              <span className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-xs rounded-full flex items-center">
                {getHiringTrendIcon(company.businessMetrics.hiringTrend)}
                {company.businessMetrics.growthRate}% Growth
              </span>
            </div>
            
            {/* Technologies */}
            {company.technologies.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-neutral-500 mb-2">Technologies:</p>
                <div className="flex flex-wrap gap-1">
                  {company.technologies.slice(0, 5).map((tech, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded"
                    >
                      {tech}
                    </span>
                  ))}
                  {company.technologies.length > 5 && (
                    <span className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-500 text-xs rounded">
                      +{company.technologies.length - 5}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Recent News */}
            {company.newsAndEvents.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-neutral-500 mb-2">Recent Activity:</p>
                <div className="space-y-1">
                  {company.newsAndEvents.slice(0, 2).map((news, index) => (
                    <div key={index} className="flex items-center space-x-2 text-xs">
                      <span className="text-sm">
                        {news.type === 'funding' && '💰'}
                        {news.type === 'product' && '🚀'}
                        {news.type === 'hiring' && '👥'}
                        {news.type === 'partnership' && '🤝'}
                        {news.type === 'acquisition' && '🏢'}
                        {news.type === 'award' && '🏆'}
                      </span>
                      <span className="text-neutral-700 dark:text-neutral-300 truncate">
                        {news.title}
                      </span>
                      <span className="text-neutral-500">
                        {new Date(news.date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Account Scoring */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-neutral-500">Account Score</span>
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {company.accountScoring.overallScore}/100
                </span>
              </div>
              <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                <div 
                  className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${company.accountScoring.overallScore}%` }}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`https://${company.website}`, '_blank')}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button
            variant="primary"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add to Campaign
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Results Section Component
const CompanyResultsSection: React.FC<CompanyResultsSectionProps> = ({
  results,
  isSearching,
  selectedCompanies,
  onCompanySelection,
  onSelectAll,
  onClearSelection,
  onBulkExport,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  if (isSearching) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600 dark:text-neutral-400">
            Searching companies with AI intelligence...
          </p>
        </div>
      </div>
    );
  }

  if (!results || results.companies.length === 0) {
    return null;
  }

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'accountScore', label: 'Account Score' },
    { value: 'buyingIntent', label: 'Buying Intent' },
    { value: 'employeeCount', label: 'Company Size' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'founded', label: 'Founded Date' },
    { value: 'lastFunding', label: 'Last Funding' }
  ];

  return (
    <div className="space-y-6">
      {/* Results Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {selectedCompanies.size > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {selectedCompanies.size} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onClearSelection}
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkExport('csv')}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkExport('json')}
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
            </div>
          )}
          
          {selectedCompanies.size === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAll}
            >
              Select All
            </Button>
          )}
        </div>
        
        {/* Sort Controls */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-neutral-600 dark:text-neutral-400">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value, sortOrder)}
            className="text-sm border border-neutral-300 dark:border-neutral-600 rounded px-3 py-1 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
          >
            <ArrowUpDown className="w-4 h-4 text-neutral-500" />
          </button>
        </div>
      </div>
      
      {/* Company Cards */}
      <div className="space-y-4">
        {results.companies.map((company) => (
          <CompanyCard
            key={company.id}
            company={company}
            isSelected={selectedCompanies.has(company.id)}
            onSelect={(selected) => onCompanySelection(company.id, selected)}
          />
        ))}
      </div>
      
      {/* Load More */}
      {results.hasMore && (
        <div className="text-center py-8">
          <Button variant="outline">
            Load More Companies
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyResultsSection; 